import { DownloadOutlined } from '@ant-design/icons';
import { TagSemanticoDTO } from '@framework/api-client';
import { useTranslation } from '@framework/i18n';
import { ModalDialog } from '@framework/ui';
import type { ButtonProps } from 'antd';
import { Button, Checkbox, Space, Typography } from 'antd';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { ExportFieldFieldsSelect } from './ExportFileFieldSelect';
import { ExportFileMultipleSemanticFieldsSelect } from './ExportFileMultipleSemanticFieldsSelect';
import { ExportFileSemanticOrderCascader } from './ExportFileSemanticOrderCascader';
import {
  type ExportFileEntity,
  type ExportFileField,
  type ExportFileType,
} from './ExportFileTypes';
import { useExportFileStorageData } from './useExportFileStorageData';

interface ExportFileButtonProps {
  type: ExportFileType;
  download: () => void;
  isFetching: boolean;
  isLoading: boolean;
  pageSize: number | undefined;
  page: number | undefined;
  entity: ExportFileEntity;
  exportAll: boolean;
  setExportAll: (value: boolean) => void;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  // Select fields
  isSelectLoading: boolean;
  onSelect: (fields: string[]) => void;
  selected: string[];
  fields: ExportFileField[] | null | undefined;
  /**
   * Ordinamenti applicati ai campi semantici selezionati (prefisso "-" per desc)
   */
  semanticOrders?: string[];
  onSemanticOrdersChange?: (value: string[]) => void;
  // Pulsante
  buttonType?: ButtonProps['type'];
  /**
   * Le esportazioni per i Bandi hanno un suffisso personalizzato
   * (ID del procedimento)
   */
  customKeySuffix?: string;
  /**
   * Campi semantici multipli da esportare come righe
   */
  multipleSemanticFields?: TagSemanticoDTO[];
  onMultipleSemanticFieldsSelect?: (fields: string[]) => void;
  selectedMultipleSemanticFields?: string[];
}

export function ExportFileButton(props: Readonly<ExportFileButtonProps>) {
  const {
    type,
    download,
    isFetching,
    isLoading,
    page,
    pageSize,
    entity,
    exportAll,
    setExportAll,
    isOpen,
    setIsOpen,
    isSelectLoading,
    onSelect,
    fields,
    selected,
    buttonType,
    customKeySuffix,
    multipleSemanticFields,
    onMultipleSemanticFieldsSelect,
    selectedMultipleSemanticFields,
    semanticOrders,
    onSemanticOrdersChange,
  } = props;

  const { t } = useTranslation();
  const isMobile = useBreakpoint(down('sm'));

  // Gestisco il caricamento dei dati dal localStorage
  const localStorageData = useExportFileStorageData({
    entity,
    type,
    onSelect,
    customKeySuffix,
  });
  const typeTitle = type === 'geojson' ? 'GeoJSON' : type.toUpperCase();

  const title = t('export-file-button.export-title', 'Esporta {{typeTitle}}', {
    typeTitle,
  });

  return (
    <>
      <Button
        aria-label={title}
        icon={isMobile && <DownloadOutlined aria-hidden="true" />}
        type={buttonType}
        onClick={() => {
          setIsOpen(true);
        }}
      >
        {!isMobile && title}
      </Button>
      <ModalDialog
        title={t(
          'export-file-button.export-procedures',
          `Esportazione {{typeTitle}} Procedimenti`,
          { typeTitle },
        )}
        open={isOpen}
        width={600}
        onCancel={e => {
          if (isFetching || isLoading) return;
          setExportAll(false);
          setIsOpen(false);
        }}
        okText={t('export-file-button.ok-text', 'Esporta')}
        onOk={() => {
          localStorageData.update(selected);
          download();
        }}
        okButtonProps={{
          loading: isFetching || isLoading,
        }}
        cancelText={t('export-file-button.cancel-text', 'Annulla')}
        cancelButtonProps={{
          disabled: isFetching || isLoading,
        }}
        maskClosable={!isLoading && !isFetching}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <ExportFieldFieldsSelect
            isLoading={isSelectLoading}
            onSelect={onSelect}
            fields={fields}
            selected={selected}
          />
          {multipleSemanticFields && multipleSemanticFields.length > 0 && (
            <ExportFileMultipleSemanticFieldsSelect
              fields={multipleSemanticFields}
              isLoading={isSelectLoading}
              onSelect={onMultipleSemanticFieldsSelect}
              selected={selectedMultipleSemanticFields}
            />
          )}
          {onSemanticOrdersChange && (
            <ExportFileSemanticOrderCascader
              fields={fields}
              selected={selected}
              value={semanticOrders ?? []}
              onChange={onSemanticOrdersChange}
              isLoading={isSelectLoading}
            />
          )}
          <Checkbox
            checked={localStorageData.saveFlag}
            onChange={v => {
              localStorageData.setSaveFlag(v.target.checked);
            }}
          >
            {t(
              'export-file-button.remember-fields',
              'Ricorda i campi per la prossima esportazione',
            )}
          </Checkbox>
          <Checkbox
            checked={!exportAll}
            onChange={v => {
              setExportAll(!v.target.checked);
            }}
          >
            {t(
              'export-file-button.download-page',
              'Scarica solo una pagina (max. {{pageSize}} {{entity}})',
              { pageSize, entity },
            )}
          </Checkbox>

          <Typography.Text>
            {t(
              'export-file-button.confirm-export',
              'Stai per esportare i dati trovati dai filtri di ricerca attualmente attivi{{exportAll}}. A seconda dei filtri utilizzati questa operazione potrebbe richiedere diverso tempo. Continuare?',
              {
                exportAll: exportAll
                  ? ''
                  : ` presenti nella pagina corrente (pag. ${page})`,
              },
            )}
          </Typography.Text>
        </Space>
      </ModalDialog>
    </>
  );
}
